import React from 'react';
import { Card } from '../components/ui';
import { Chip, MiniArea, useChartWidth } from '../components/charts';
import { BLUE_PRIMARY } from '../theme';

const INSIGHT_TONE_STYLES = {
  initial: {
    border: '#bfdbfe',
    background: '#f8fbff',
    accent: '#2563eb',
    badgeBg: '#dbeafe',
    badgeColor: '#1d4ed8',
  },
  improved: {
    border: '#bbf7d0',
    background: '#f0fdf4',
    accent: '#16a34a',
    badgeBg: '#dcfce7',
    badgeColor: '#15803d',
  },
  high_performance: {
    border: '#bbf7d0',
    background: '#f7fee7',
    accent: '#22c55e',
    badgeBg: '#dcfce7',
    badgeColor: '#15803d',
  },
  warning: {
    border: '#fde68a',
    background: '#fffbeb',
    accent: '#d97706',
    badgeBg: '#fef3c7',
    badgeColor: '#b45309',
  },
  dropped: {
    border: '#fecaca',
    background: '#fef2f2',
    accent: '#ef4444',
    badgeBg: '#fee2e2',
    badgeColor: '#b91c1c',
  },
  stable: {
    border: '#dbeafe',
    background: '#f8fafc',
    accent: '#3b82f6',
    badgeBg: '#e0f2fe',
    badgeColor: '#0369a1',
  },
};

export default function PerformanceSummary({ metric, setMetric, series, seriesDates, avg, trend, bestWorst, analysisInsight }) {
  const width = useChartWidth(50);
  const latest = series && series.length ? series[series.length - 1] : null;
  const prev = series && series.length > 1 ? series[series.length - 2] : null;
  const diff = (latest != null && prev != null) ? (latest - prev) : null;
  const betterWhenHigher = metric !== 'rank';
  const isUp = diff != null ? diff > 0 : null;
  const isGood = diff != null ? ((betterWhenHigher && isUp) || (!betterWhenHigher && !isUp)) : null;
  const trendColor = isGood == null ? '#64748b' : (isGood ? '#10B981' : '#EF4444');
  const arrow = isUp == null ? '' : (isUp ? '▲' : '▼');
  const insightTone = (analysisInsight && INSIGHT_TONE_STYLES[analysisInsight.tone]) || INSIGHT_TONE_STYLES.stable;
  const subjectCards = [
    bestWorst?.best ? {
      key: 'best',
      title: 'En İyi Ders',
      dotColor: '#10B981',
      titleColor: '#059669',
      subject: bestWorst.best,
    } : null,
    bestWorst?.worst ? {
      key: 'worst',
      title: 'Gelişmesi Gereken',
      dotColor: '#EF4444',
      titleColor: '#B91C1C',
      subject: bestWorst.worst,
    } : null,
  ].filter(Boolean);

  const formatValue = (val) => {
    if (val == null) return '-';
    if (metric === 'net') return Number(val).toFixed(2);
    if (metric === 'rank') return String(Math.round(val));
    return Number(val).toFixed(1);
  };

  const formatDiff = (value) => {
    if (value == null) return '-';
    if (metric === 'net') return Math.abs(value).toFixed(2);
    if (metric === 'rank') return String(Math.abs(Math.round(value)));
    return Math.abs(value).toFixed(1);
  };

  return (
    <Card title="Performans Özeti">
      {subjectCards.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${subjectCards.length}, minmax(0, 1fr))`,
            gap: 8,
            marginTop: 2,
            marginBottom: 10,
          }}
        >
          {subjectCards.map((card) => (
            <div
              key={card.key}
              style={{
                background: '#fff',
                border: '1px solid #eef2f7',
                borderRadius: 10,
                padding: 10,
                boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: card.dotColor, display: 'inline-block' }} />
                  <span style={{ fontSize: 12, color: card.titleColor, fontWeight: 700 }}>{card.title}</span>
                </div>
                <span style={{ fontSize: 12, color: '#64748b' }}>Net</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.1, marginBottom: 4 }}>{card.subject.name}</div>
                  <div style={{ fontWeight: 700, color: '#0F1E56', fontSize: 14 }}>{card.subject.value.toFixed(2)}</div>
                </div>
                <div>
                  {(() => {
                    const subjectDelta = card.subject.delta;
                    const subjectIsUp = subjectDelta != null ? subjectDelta > 0 : null;
                    const chipColor = subjectIsUp == null ? '#64748b' : (subjectIsUp ? '#10B981' : '#EF4444');
                    const chipArrow = subjectIsUp == null ? '' : (subjectIsUp ? '▲' : '▼');
                    return (
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          border: '1px solid #e5e7eb',
                          fontSize: 12,
                          color: chipColor,
                          background: '#f8fafc',
                        }}
                      >
                        {chipArrow} {subjectDelta != null ? Math.abs(subjectDelta).toFixed(2) : '-'}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {analysisInsight && (
        <div
          style={{
            position: 'relative',
            border: `1px solid ${insightTone.border}`,
            background: insightTone.background,
            borderRadius: 12,
            padding: '12px 12px 12px 14px',
            marginBottom: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              background: insightTone.accent,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <div
                style={{
                  background: insightTone.badgeBg,
                  color: insightTone.badgeColor,
                  borderRadius: 999,
                  padding: '4px 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Son Deneme Analizi
              </div>
            </div>
            {analysisInsight.dateLabel ? (
              <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                {analysisInsight.dateLabel}
              </div>
            ) : null}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: '#0f172a', fontWeight: 700 }}>
              {analysisInsight.summary}
            </div>
            {analysisInsight.subjectText ? (
              <div style={{ fontSize: 13, lineHeight: 1.5, color: '#334155' }}>
                {analysisInsight.subjectText}
              </div>
            ) : null}
            {analysisInsight.actionText ? (
              <div style={{ fontSize: 12, lineHeight: 1.5, color: '#475569' }}>
                {analysisInsight.actionText}
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div
        style={{
          background: '#fff',
          border: '1px solid #eef2f7',
          borderLeft: `4px solid ${BLUE_PRIMARY}`,
          borderRadius: 12,
          padding: 8,
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <button onClick={() => setMetric('score')} style={{ background: 'transparent', border: 'none', padding: 0 }}><Chip label="Toplam Puan" active={metric === 'score'} /></button>
          <button onClick={() => setMetric('net')} style={{ background: 'transparent', border: 'none', padding: 0 }}><Chip label="Net" active={metric === 'net'} /></button>
          <button onClick={() => setMetric('rank')} style={{ background: 'transparent', border: 'none', padding: 0 }}><Chip label="Sıra" active={metric === 'rank'} /></button>
        </div>
        <MiniArea values={series} dates={seriesDates} showAvg={metric !== 'rank'} avg={avg} width={width} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Ortalama: {avg != null ? formatValue(avg) : '-'}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 12, color: '#334155', background: '#f8fafc' }}>
              Son: {formatValue(latest)}
            </span>
            <span style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 12, color: trendColor, background: '#f8fafc' }}>
              {arrow} {formatDiff(diff)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
